import { Badge } from '@repo/ui/components/ui/badge'
import { Button } from '@repo/ui/components/ui/button'
import { Callout } from '@repo/ui/components/ui/callout'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@repo/ui/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/ui/select'
import { createFileRoute } from '@tanstack/react-router'
import { formatDistanceToNow } from 'date-fns'
import { Inbox, Mail, RefreshCcw } from 'lucide-react'
import { toast } from 'sonner'
import { ReportRouteShell } from '@/components/app/route-shells'
import type { RouteStaticData } from '@/lib/router/breadcrumbs'
import { trpc } from '@/lib/trpc'

export const Route = createFileRoute('/_app/admin/support')({
  component: AdminSupportPage,
  staticData: {
    breadcrumbs: [{ label: 'Support' }],
  } satisfies RouteStaticData,
})

const STATUS_LABELS: Record<string, string> = {
  open: 'Open',
  in_progress: 'In progress',
  resolved: 'Resolved',
}

function AdminSupportPage() {
  const utils = trpc.useUtils()
  const supportQuery = trpc.admin.support.list.useQuery({ page: 1, limit: 50 })
  const statsQuery = trpc.admin.support.stats.useQuery()
  const updateStatus = trpc.admin.support.updateStatus.useMutation({
    onSuccess: async () => {
      toast.success('Support status updated')
      await Promise.all([
        utils.admin.support.list.invalidate(),
        utils.admin.support.stats.invalidate(),
      ])
    },
    onError: (error) => toast.error(error.message || 'Unable to update support status'),
  })

  const submissions = supportQuery.data?.submissions ?? []
  const stats = statsQuery.data

  return (
    <ReportRouteShell>
      <div className="grid gap-6">
        <div className="grid gap-4 md:grid-cols-4">
          <SupportMetric title="Active" value={stats?.active ?? 0} description="Needs attention" />
          <SupportMetric title="Open" value={stats?.open ?? 0} description="New requests" />
          <SupportMetric
            title="In progress"
            value={stats?.inProgress ?? 0}
            description="Being handled"
          />
          <SupportMetric title="Resolved" value={stats?.resolved ?? 0} description="Closed out" />
        </div>

        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Inbox className="size-5 text-primary" aria-hidden="true" />
                Support inbox
              </CardTitle>
              <CardDescription>
                Operator help-sheet feedback and marketing contact form submissions.
              </CardDescription>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => supportQuery.refetch()}
              disabled={supportQuery.isFetching}
            >
              <RefreshCcw className="mr-2 size-4" aria-hidden="true" />
              Refresh
            </Button>
          </CardHeader>
          <CardContent>
            {supportQuery.isError ? (
              <Callout variant="destructive">
                <p className="text-sm text-muted-foreground">
                  Failed to load support inbox. {supportQuery.error.message}
                </p>
              </Callout>
            ) : submissions.length === 0 ? (
              <div className="grid place-items-center rounded-[8px] border border-dashed bg-muted/25 px-4 py-12 text-center">
                <Mail className="size-8 text-muted-foreground" aria-hidden="true" />
                <h2 className="mt-3 text-lg font-semibold">No support requests yet</h2>
                <p className="mt-1 max-w-md text-sm text-muted-foreground">
                  New operator feedback and public contact messages will appear here after they are
                  emailed to the admin inbox.
                </p>
              </div>
            ) : (
              <div className="divide-y rounded-[8px] border">
                {submissions.map((submission) => (
                  <article key={submission.id} className="grid gap-4 p-4 lg:grid-cols-[1fr_180px]">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="truncate font-semibold">{submission.subject}</h2>
                        <Badge variant={submission.status === 'resolved' ? 'secondary' : 'default'}>
                          {STATUS_LABELS[submission.status] ?? submission.status}
                        </Badge>
                        <Badge variant="outline">{formatSource(submission.source)}</Badge>
                      </div>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {submission.name} ·{' '}
                        <a
                          className="underline-offset-4 hover:underline"
                          href={`mailto:${submission.email}`}
                        >
                          {submission.email}
                        </a>
                      </p>
                      <p className="mt-3 whitespace-pre-wrap text-sm leading-6">
                        {submission.message}
                      </p>
                      <p className="mt-3 text-xs text-muted-foreground">
                        Submitted {formatRelative(submission.createdAt)}
                      </p>
                    </div>
                    <div className="grid content-start gap-2">
                      <label
                        className="text-xs font-medium text-muted-foreground"
                        htmlFor={`status-${submission.id}`}
                      >
                        Status
                      </label>
                      <Select
                        value={submission.status}
                        onValueChange={(status) =>
                          updateStatus.mutate({
                            id: submission.id,
                            status: status as 'open' | 'in_progress' | 'resolved',
                          })
                        }
                        disabled={updateStatus.isPending}
                      >
                        <SelectTrigger id={`status-${submission.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="open">Open</SelectItem>
                          <SelectItem value="in_progress">In progress</SelectItem>
                          <SelectItem value="resolved">Resolved</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </ReportRouteShell>
  )
}

function SupportMetric({
  title,
  value,
  description,
}: {
  title: string
  value: number
  description: string
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-semibold">{value}</div>
        <p className="text-xs text-muted-foreground">{description}</p>
      </CardContent>
    </Card>
  )
}

function formatSource(source: string) {
  return source === 'operator_help' ? 'Operator help' : 'Public contact'
}

function formatRelative(value: string | Date) {
  return formatDistanceToNow(new Date(value), { addSuffix: true })
}
